// CRM-native Find similar — serves both companies and contacts. Replaces the classic UI jump.
import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';
import { creditAwareFetch } from '../../hooks/creditAwareFetch';

function initials(name) {
  const p = String(name || '').trim().split(/\s+/);
  return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || '?';
}
function Cofav({ name, website }) {
  const [failed, setFailed] = useState(false);
  const dom = website ? String(website).replace(/^https?:\/\/(www\.)?/, '').split('/')[0] : '';
  if (!dom || failed) return <span className="crmr-cofav" style={{ background: 'linear-gradient(135deg,#FF7A59,#EC4899)' }}>{initials(name)}</span>;
  return <span className="crmr-cofav"><img src={`https://www.google.com/s2/favicons?domain=${dom}&sz=64`} alt="" onError={() => setFailed(true)} /></span>;
}

export default function CrmFindSimilar() {
  const { entity, seedId } = useParams();
  const navState = (typeof window !== 'undefined' && window.history && window.history.state && window.history.state.usr) || {};
  const navigate = useNavigate();
  const { clientId } = useOutletContext();
  const { getToken } = useAuth(); // eslint-disable-line no-unused-vars

  const isCompany = entity === 'companies';
  const [seed, setSeed] = useState(null);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [running, setRunning] = useState(false);
  const [importing, setImporting] = useState(false);
  const [ran, setRan] = useState(false);

  const run = useCallback(async () => {
    setRunning(true); setResults([]); setSelected(new Set());
    const tId = toast.loading('Finding similar ' + (isCompany ? 'companies' : 'contacts') + '…');
    try {
      const url = isCompany
        ? `${API_BASE}/companies/${seedId}/find-similar`
        : `${API_BASE}/contacts/${seedId}/find-similar`;
      const res = await creditAwareFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
      if (!res) { toast.dismiss(tId); setRunning(false); return; }
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setSeed(data.seed || null);
      setResults(Array.isArray(data.results) ? data.results : []);
      setRan(true);
      const n = (data.results || []).length;
      toast.success(n ? `Found ${n}` : 'No matches', { id: tId });
    } catch (e) { toast.error('Search failed: ' + e.message, { id: tId }); }
    setRunning(false);
  }, [isCompany, seedId]);

  const ranFor = useRef(null);
  useEffect(() => {
    const key = `${entity}/${seedId}`;
    if (ranFor.current === key) return;
    ranFor.current = key;
    run();
  }, [entity, seedId, run]);

  const toggle = (i) => setSelected(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const selectAllNew = () => setSelected(new Set(results.map((r, i) => importableIndex(r) ? i : null).filter(i => i !== null)));
  const importableIndex = (r) => isCompany ? !r.already_exists : !!r.contact_suggestion;

  async function importSelected() {
    if (!selected.size) return;
    setImporting(true);
    const n = selected.size;
    const tId = toast.loading(`Importing ${n}…`);
    try {
      if (isCompany) {
        let added = 0, dup = 0;
        for (const i of selected) {
          const r = results[i];
          const res = await fetch(`${API_BASE}/clients/${clientId}/companies`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: r.name, website: r.website, sector: r.sector || '', size: '', location: r.location || '' }),
          });
          if (res.status === 201) added++; else if (res.status === 409) dup++;
        }
        toast.success(`${added} added${dup ? `, ${dup} already existed` : ''}`, { id: tId });
      } else {
        const pairs = [...selected].map(i => {
          const r = results[i];
          return { company: r.company, contact: r.contact_suggestion };
        });
        const res = await creditAwareFetch(`${API_BASE}/clients/${clientId}/find-similar-contacts/import`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pairs }),
        });
        if (!res) { setImporting(false); return; }
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        toast.success(`Imported ${data.added ?? n}`, { id: tId });
      }
      setSelected(new Set());
    } catch (e) { toast.error('Import failed: ' + e.message, { id: tId }); }
    setImporting(false);
  }

  const back = () => navigate(`/clients/${clientId}/crm/${isCompany ? 'companies' : 'contacts'}/${seedId}`);

  return (
    <div className="crmr-wrap">
      <div className="crmr-topbar">
        <button className="crmr-back" onClick={back}>← Back</button>
        <span className="crmr-crumb">Find similar {isCompany ? 'companies' : 'contacts'}{(() => { if (navState.seedLabel) return ` to ${navState.seedLabel}`; if (isCompany) return seed ? ` to ${seed.name || ''}` : ''; if (seed) return ` to ${[seed.role, seed.company].filter(Boolean).join(' at ')}`; return ''; })()}</span>
      </div>

      <div className="crmr-actionbar">
        <button className="crmr-act crmr-act-primary" onClick={run} disabled={running}>{running ? 'Searching…' : 'Run again'}</button>
        {selected.size > 0 && <button className="crmr-act crmr-act-blue" onClick={importSelected} disabled={importing}>{importing ? 'Importing…' : `Import ${selected.size}`}</button>}
        {results.length > 0 && <button className="crmr-act crmr-act-ghost" onClick={selectAllNew}>Select all</button>}
        {selected.size > 0 && <button className="crmr-act crmr-act-ghost" onClick={() => setSelected(new Set())}>Clear</button>}
      </div>

      <div className="crmfs-list">
        {running && <div className="crmr-empty">Searching…</div>}
        {!running && ran && results.length === 0 && <div className="crmr-empty">No similar {isCompany ? 'companies' : 'contacts'} found.</div>}

        {!running && results.map((r, i) => {
          const importable = importableIndex(r);
          const on = selected.has(i);
          if (isCompany) {
            return (
              <div key={i} className={'crmfs-card' + (on ? ' crmfs-card--on' : '') + (importable ? '' : ' crmfs-card--dim')} onClick={() => importable && toggle(i)}>
                <input type="checkbox" className="crmfs-check" checked={on} disabled={!importable} onChange={() => {}} />
                <Cofav name={r.name} website={r.website} />
                <div className="crmfs-body">
                  <div className="crmfs-name">{r.name}{r.already_exists && <span className="crmr-pill crmr-p-green crmfs-tag">In workspace</span>}</div>
                  {r.description && <div className="crmfs-desc">{r.description}</div>}
                  {r.website && <a className="crmr-link crmfs-web" href={r.website.startsWith('http') ? r.website : 'https://' + r.website} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>{r.domain || r.website}</a>}
                </div>
              </div>
            );
          }
          const cs = r.contact_suggestion;
          const personName = cs ? cs.name : null;
          return (
            <div key={i} className={'crmfs-card' + (on ? ' crmfs-card--on' : '') + (importable ? '' : ' crmfs-card--dim')} onClick={() => importable && toggle(i)}>
              <input type="checkbox" className="crmfs-check" checked={on} disabled={!importable} onChange={() => {}} />
              <span className="crmr-avatar crmfs-person-av" style={{ background: 'linear-gradient(135deg,#7C5CFC,#2E8FFF)' }}>{cs ? initials(personName) : '—'}</span>
              <div className="crmfs-body">
                {cs ? (
                  <>
                    <div className="crmfs-name">{personName}{cs.title ? <span className="crmfs-role"> · {cs.title}</span> : null}</div>
                    <div className="crmfs-person">at {r.company?.name}{r.company?.location ? ` · ${r.company.location}` : ''}</div>
                    {cs.linkedin_url && <a className="crmr-link crmfs-web" href={cs.linkedin_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>LinkedIn profile →</a>}
                  </>
                ) : (
                  <>
                    <div className="crmfs-name crmfs-nomatchname">{r.company?.name}</div>
                    <div className="crmfs-desc crmfs-nomatch">No contact found at this company yet</div>
                    {r.company?.website && <a className="crmr-link crmfs-web" href={r.company.website.startsWith('http') ? r.company.website : 'https://' + r.company.website} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>{r.company.website}</a>}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
